import { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import SaleSkipLoginPage from "./components/SaleSkipLoginPage";
import WalletAccess from "./components/WalletAccess";

const API_BASE_URL = "https://data-transfer-backend.onrender.com";
const CATEGORY_VIEW_MAP = {
  General: "active",
  Archive: "archive",
  "Recently Deleted": "deleted",
};

function getFallbackTitleFromText(text) {
  const cleanText = (text || "").trim();
  if (!cleanText) {
    return "Untitled note";
  }

  const firstLine = cleanText.split("\n")[0];
  return firstLine;
}

function getDisplayTitle(note, maxLength = 42) {
  const baseTitle =
    (note?.title || "").trim() || getFallbackTitleFromText(note?.text);
  return baseTitle.length > maxLength
    ? `${baseTitle.slice(0, maxLength)}...`
    : baseTitle;
}

function getNotePreview(text) {
  const cleanText = (text || "").replace(/\s+/g, " ").trim();
  if (!cleanText) {
    return "No text yet";
  }

  return cleanText.length > 72 ? `${cleanText.slice(0, 72)}...` : cleanText;
}

function formatTimestamp(value) {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleString();
}

function formatCoordinate(value) {
  return typeof value === "number" ? value.toFixed(6) : "N/A";
}

function getDirectionsUrl(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

function getEmptyCategoryMessage(category) {
  if (category === "Archive") {
    return "No archived notes.";
  }
  if (category === "Recently Deleted") {
    return "No recently deleted notes.";
  }
  return "No notes found. Create your first note.";
}

function App() {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [userId, setUserId] = useState("");

  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState("new");
  const [editorTitle, setEditorTitle] = useState("");
  const [editorText, setEditorText] = useState("");
  const [editorPhoto, setEditorPhoto] = useState(null);

  const [notesLoading, setNotesLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const [statusMessage, setStatusMessage] = useState("");

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [loginLogs, setLoginLogs] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("General");

  const activeView = CATEGORY_VIEW_MAP[activeCategory] || "active";
  const isDeletedCategory = activeCategory === "Recently Deleted";
  const isArchiveCategory = activeCategory === "Archive";
  const isWalletCategory = activeCategory === "Wallet";
  const canCreateInCategory = activeCategory === "General";

  const activeNote = useMemo(
    () => notes.find((note) => note._id === activeNoteId) || null,
    [notes, activeNoteId]
  );
  const isCreating = activeNoteId === "new";

  const loadNotes = useCallback(async () => {
    if (!unlocked || isWalletCategory) {
      return;
    }

    setNotesLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/clipboard/get?pin=${encodeURIComponent(
          pin
        )}&view=${activeView}`
      );
      const data = await res.json().catch(() => []);

      if (!res.ok) {
        setStatusMessage(data.message || "Could not load notes.");
        setNotes([]);
        setActiveNoteId("new");
        setEditorTitle("");
        setEditorText("");
        setEditorPhoto(null);
        return;
      }

      const list = Array.isArray(data) ? data : [];
      setNotes(list);
      setActiveNoteId((previousId) => {
        if (previousId === "new" && canCreateInCategory) {
          return "new";
        }

        if (list.some((note) => note._id === previousId)) {
          return previousId;
        }

        return list[0]?._id || "new";
      });

      if (list.length === 0) {
        setEditorTitle("");
        setEditorText("");
        setEditorPhoto(null);
      }
    } catch (error) {
      setStatusMessage("Could not load notes.");
    } finally {
      setNotesLoading(false);
    }
  }, [unlocked, pin, activeView, canCreateInCategory, isWalletCategory]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    if (activeNoteId === "new") {
      if (!canCreateInCategory) {
        setEditorText("");
      }
      setEditorTitle("");
      setEditorPhoto(null);
      return;
    }

    const selectedNote = notes.find((note) => note._id === activeNoteId);
    if (!selectedNote) {
      return;
    }

    setEditorTitle(
      (selectedNote.title || "").trim() || getFallbackTitleFromText(selectedNote.text)
    );
    setEditorText(selectedNote.text || "");
    setEditorPhoto(null);
  }, [activeNoteId, notes, canCreateInCategory]);

  function handleLoginSuccess({ password, userId: loggedInUserId }) {
    setPin(password || "");
    setUserId(loggedInUserId || "");
    setUnlocked(true);
    setStatusMessage("");
    setActiveCategory("General");
    setIsSidebarOpen(false);
  }

  function startNewNote() {
    if (!canCreateInCategory) {
      setStatusMessage("New notes can only be created in General.");
      return;
    }

    setActiveNoteId("new");
    setEditorTitle("");
    setEditorText("");
    setEditorPhoto(null);
    setStatusMessage("");
  }

  function selectNote(note) {
    setActiveNoteId(note._id);
    setEditorTitle((note.title || "").trim() || getFallbackTitleFromText(note.text));
    setEditorText(note.text || "");
    setEditorPhoto(null);
    setStatusMessage("");
  }

  function selectCategory(name) {
    setActiveCategory(name);
    setActiveNoteId("new");
    setEditorTitle("");
    setEditorText("");
    setEditorPhoto(null);
    setIsSidebarOpen(false);
    setStatusMessage("");
  }

  async function saveNote() {
    if (isDeletedCategory) {
      setStatusMessage("Deleted notes cannot be edited.");
      return;
    }

    if (!editorText.trim() && !editorPhoto) {
      setStatusMessage("Add text or an image before saving.");
      return;
    }

    if (isCreating && !canCreateInCategory) {
      setStatusMessage("New notes can only be created in General.");
      return;
    }

    setSaveLoading(true);

    try {
      const normalizedTitle =
        (editorTitle || "").trim() || getFallbackTitleFromText(editorText);
      const formData = new FormData();
      formData.append("title", normalizedTitle);
      formData.append("text", editorText);
      formData.append("pin", pin);

      if (editorPhoto) {
        formData.append("photo", editorPhoto);
      }

      if (isCreating) {
        const res = await fetch(`${API_BASE_URL}/clipboard/save`, {
          method: "POST",
          body: formData,
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setStatusMessage(data.message || "Could not create note.");
          return;
        }

        setActiveNoteId(data.note?._id || "new");
        setStatusMessage("Note created.");
      } else {
        const res = await fetch(`${API_BASE_URL}/clipboard/update/${activeNoteId}`, {
          method: "PUT",
          body: formData,
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setStatusMessage(data.message || "Could not update note.");
          return;
        }

        setStatusMessage("Note updated.");
      }

      setEditorPhoto(null);
      await loadNotes();
    } catch (error) {
      setStatusMessage("Could not save note.");
    } finally {
      setSaveLoading(false);
    }
  }

  async function toggleArchiveActiveNote() {
    if (isCreating || !activeNote) {
      setStatusMessage("Select a saved note first.");
      return;
    }

    if (isDeletedCategory) {
      setStatusMessage("Deleted notes cannot be archived.");
      return;
    }

    setActionLoading(true);
    try {
      const route = isArchiveCategory ? "unarchive" : "archive";
      const res = await fetch(`${API_BASE_URL}/clipboard/${route}/${activeNote._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatusMessage(data.message || "Could not move note.");
        return;
      }

      setStatusMessage(
        isArchiveCategory ? "Note moved to General." : "Note archived."
      );
      setActiveNoteId("new");
      setEditorTitle("");
      setEditorText("");
      setEditorPhoto(null);
      await loadNotes();
    } catch (error) {
      setStatusMessage("Could not move note.");
    } finally {
      setActionLoading(false);
    }
  }

  async function deleteActiveNote() {
    if (isCreating || !activeNote) {
      setStatusMessage("Select a saved note first.");
      return;
    }

    const confirmMessage = isDeletedCategory
      ? "Delete this note permanently?"
      : "Move this note to Recently Deleted?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setActionLoading(true);
    try {
      const route = isDeletedCategory ? "hard-delete" : "delete";
      const res = await fetch(
        `${API_BASE_URL}/clipboard/${route}/${activeNote._id}?pin=${encodeURIComponent(
          pin
        )}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatusMessage(data.message || "Could not delete note.");
        return;
      }

      if (isDeletedCategory) {
        setStatusMessage("Note permanently deleted.");
      } else {
        setStatusMessage("Note moved to Recently Deleted.");
      }
      setActiveNoteId("new");
      setEditorTitle("");
      setEditorText("");
      setEditorPhoto(null);
      await loadNotes();
    } catch (error) {
      setStatusMessage("Could not delete note.");
    } finally {
      setActionLoading(false);
    }
  }

  async function restoreActiveNote() {
    if (!isDeletedCategory || isCreating || !activeNote) {
      setStatusMessage("Select a deleted note to restore.");
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/clipboard/restore/${activeNote._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatusMessage(data.message || "Could not restore note.");
        return;
      }

      setStatusMessage("Note restored to General.");
      setActiveCategory("General");
      setActiveNoteId("new");
      setEditorTitle("");
      setEditorText("");
      setEditorPhoto(null);
    } catch (error) {
      setStatusMessage("Could not restore note.");
    } finally {
      setActionLoading(false);
    }
  }

  async function deleteAllRecentlyDeleted() {
    if (!isDeletedCategory) {
      return;
    }

    if (notes.length === 0) {
      setStatusMessage("No notes to delete.");
      return;
    }

    if (!window.confirm("Delete all notes from Recently Deleted permanently?")) {
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/clipboard/clear-deleted?pin=${encodeURIComponent(pin)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatusMessage(data.message || "Could not clear Recently Deleted.");
        return;
      }

      setStatusMessage("Recently Deleted cleared.");
      setActiveNoteId("new");
      setEditorTitle("");
      setEditorText("");
      setEditorPhoto(null);
      await loadNotes();
    } catch (error) {
      setStatusMessage("Could not clear Recently Deleted.");
    } finally {
      setActionLoading(false);
    }
  }

  async function copyCurrentNote() {
    const textToCopy = editorText?.trim();
    if (!textToCopy) {
      setStatusMessage("No note text to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setStatusMessage("Note copied.");
    } catch (error) {
      setStatusMessage("Could not copy note.");
    }
  }

  async function downloadActiveImage() {
    if (!activeNote?.photo) {
      setStatusMessage("No image available for this note.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/uploads/${activeNote.photo}`);
      if (!res.ok) {
        setStatusMessage("Could not download image.");
        return;
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = activeNote.photo;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
      setStatusMessage("Image downloaded.");
    } catch (error) {
      setStatusMessage("Could not download image.");
    }
  }

  async function openLogs() {
    if (!userId) {
      setStatusMessage("User session not found.");
      return;
    }

    setLogsLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/logs?userId=${encodeURIComponent(userId)}`
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatusMessage(data.message || "Could not fetch login logs.");
        return;
      }

      setLoginLogs(Array.isArray(data.logs) ? data.logs : []);
      setShowLogsModal(true);
    } catch (error) {
      setStatusMessage("Could not fetch login logs.");
    } finally {
      setLogsLoading(false);
    }
  }

  function lockVault() {
    setUnlocked(false);
    setPin("");
    setUserId("");
    setNotes([]);
    setActiveNoteId("new");
    setEditorTitle("");
    setEditorText("");
    setEditorPhoto(null);
    setStatusMessage("");
    setShowLogsModal(false);
    setLoginLogs([]);
    setIsSidebarOpen(false);
    setActiveCategory("General");
  }

  if (!unlocked) {
    return (
      <SaleSkipLoginPage
        apiBaseUrl={API_BASE_URL}
        onLoginSuccess={handleLoginSuccess}
      />
    );
  }

  return (
    <div className={`workspace-shell ${isSidebarOpen ? "" : "sidebar-collapsed"}`}>
      <aside className="sidebar">
        <div>
          <div className="brand-block">
            <div className="brand-avatar">SV</div>
            <div>
              <p className="brand-name">Surya Notes</p>
              <p className="brand-caption">Personal knowledge base</p>
            </div>
          </div>

          <div className="sidebar-group">
            <p className="sidebar-heading">Categories</p>
            <button
              type="button"
              className={`sidebar-link ${activeCategory === "General" ? "active" : ""}`}
              onClick={() => selectCategory("General")}
            >
              General
            </button>
            <button
              type="button"
              className={`sidebar-link ${activeCategory === "Archive" ? "active" : ""}`}
              onClick={() => selectCategory("Archive")}
            >
              Archive
            </button>
            <button
              type="button"
              className={`sidebar-link ${
                activeCategory === "Recently Deleted" ? "active" : ""
              }`}
              onClick={() => selectCategory("Recently Deleted")}
            >
              Recently Deleted
            </button>
          </div>

          <div className="sidebar-group">
            <p className="sidebar-heading">Actions</p>
            <button
              type="button"
              className={`sidebar-link ${activeCategory === "Wallet" ? "active" : ""}`}
              onClick={() => selectCategory("Wallet")}
            >
              Wallet
            </button>
            <button
              type="button"
              className="sidebar-link"
              onClick={() => {
                openLogs();
                setIsSidebarOpen(false);
              }}
              disabled={logsLoading}
            >
              {logsLoading ? "Loading logs..." : "Show Logs"}
            </button>
            <button
              type="button"
              className="sidebar-link danger"
              onClick={() => {
                setIsSidebarOpen(false);
                lockVault();
              }}
            >
              Lock Vault
            </button>
          </div>
        </div>

      </aside>

      {isWalletCategory ? (
        <section className="wallet-panel-shell">
          <WalletAccess apiBaseUrl={API_BASE_URL} />
        </section>
      ) : (
        <>
          <section className="notes-panel">
            <div className="notes-panel-header">
              <div className="notes-panel-title-wrap">
                <div>
                  <p className="panel-kicker">{activeCategory}</p>
                  <h2>Notes</h2>
                </div>
              </div>
              {canCreateInCategory && (
                <button type="button" className="new-note-btn" onClick={startNewNote}>
                  + New
                </button>
              )}
              {isDeletedCategory && (
                <button
                  type="button"
                  className="ghost-danger-btn"
                  onClick={deleteAllRecentlyDeleted}
                  disabled={actionLoading || notes.length === 0}
                >
                  Delete All
                </button>
              )}
            </div>

            <div className="notes-list">
              {notesLoading && <p className="muted-text">Loading notes...</p>}
              {!notesLoading && notes.length === 0 && (
                <p className="muted-text">{getEmptyCategoryMessage(activeCategory)}</p>
              )}

              {notes.map((note) => (
                <button
                  key={note._id}
                  type="button"
                  className={`note-item ${note._id === activeNoteId ? "selected" : ""}`}
                  onClick={() => selectNote(note)}
                >
                  <div className="note-item-top">
                    <h3>{getDisplayTitle(note)}</h3>
                    <span>
                      {new Date(note.updatedAt || note.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p>{getNotePreview(note.text)}</p>
                </button>
              ))}
            </div>
          </section>

          <main className="editor-panel">
            <div className="editor-top">
              <p className="panel-kicker">
                {isDeletedCategory ? "Read only" : isCreating ? "New note" : "Editor"}
              </p>
              {statusMessage && <p className="status-chip">{statusMessage}</p>}
            </div>

            <input
              className="editor-title-input"
              value={editorTitle}
              onChange={(event) => setEditorTitle(event.target.value)}
              placeholder={isDeletedCategory ? "Deleted note title" : "Note title"}
              readOnly={isDeletedCategory}
            />

            <textarea
              className="editor-input"
              value={editorText}
              onChange={(event) => setEditorText(event.target.value)}
              placeholder={
                isDeletedCategory
                  ? "Deleted note preview"
                  : "Write your note here..."
              }
              readOnly={isDeletedCategory}
            />

            <div className="editor-actions">
              {isDeletedCategory ? (
                <>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={restoreActiveNote}
                    disabled={actionLoading || isCreating}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    className="ghost-danger-btn"
                    onClick={deleteActiveNote}
                    disabled={actionLoading || isCreating}
                  >
                    Delete Permanently
                  </button>
                </>
              ) : (
                <>
                  <label className="file-picker">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => setEditorPhoto(event.target.files?.[0] || null)}
                    />
                    {editorPhoto ? editorPhoto.name : "Attach image"}
                  </label>

                  <button
                    type="button"
                    className="primary-btn"
                    onClick={saveNote}
                    disabled={saveLoading}
                  >
                    {saveLoading ? "Saving..." : isCreating ? "Save Note" : "Update Note"}
                  </button>

                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={copyCurrentNote}
                    disabled={!editorText.trim()}
                  >
                    Copy Note
                  </button>

                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={downloadActiveImage}
                    disabled={!activeNote?.photo}
                  >
                    Download Image
                  </button>

                  {!isCreating && (
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={toggleArchiveActiveNote}
                      disabled={actionLoading}
                    >
                      {isArchiveCategory ? "Move to General" : "Archive"}
                    </button>
                  )}

                  <button
                    type="button"
                    className="ghost-danger-btn"
                    onClick={deleteActiveNote}
                    disabled={actionLoading || isCreating}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>

            {activeNote?.photo && (
              <div className="image-preview">
                <img
                  src={`${API_BASE_URL}/uploads/${activeNote.photo}`}
                  alt="note attachment"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}
          </main>
        </>
      )}

      <button
        type="button"
        className="sidebar-fab"
        onClick={() => setIsSidebarOpen((prevState) => !prevState)}
        aria-label="Toggle menu"
      >
        Menu
      </button>

      {isSidebarOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close menu overlay"
        />
      )}

      {showLogsModal && (
        <div className="modal-backdrop" onClick={() => setShowLogsModal(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Recent Login Activity</h3>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setShowLogsModal(false)}
              >
                Close
              </button>
            </div>

            {loginLogs.length === 0 ? (
              <p className="muted-text">No login activity available.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Timestamp</th>
                      <th>Latitude</th>
                      <th>Longitude</th>
                      <th>Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginLogs.map((log, index) => {
                      const directionsUrl = getDirectionsUrl(
                        log.latitude,
                        log.longitude
                      );

                      return (
                        <tr key={`${log.timestamp}-${index}`}>
                          <td>
                            <span
                              className={`log-pill ${
                                log.status === "success" ? "ok" : "fail"
                              }`}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td>{formatTimestamp(log.timestamp)}</td>
                          <td>{formatCoordinate(log.latitude)}</td>
                          <td>{formatCoordinate(log.longitude)}</td>
                          <td>
                            {directionsUrl ? (
                              <a
                                className="map-link-btn"
                                href={directionsUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Directions
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
